// Dependencies
import { useSnackbar } from "notistack";
import { useInfiniteQuery, useQuery } from "react-query";
import { FC, SyntheticEvent, useEffect, useRef, useState } from "react";

// Components
import { Choose, When, If } from "jsx-control-statements";
import { MenuItem, SelectChangeEvent } from "@mui/material";
import { BarLoading, HashLoading } from "~/components/Common";
import MonthTab from "../MonthTab";
import TransactionAdd from "../TransactionAdd";
import DayTransactions from "../DayTransactions";

// Icons
import AddIcon from "@mui/icons-material/Add";

// Types
import { QUERY_KEY } from "~/enums";
import { TQueryError } from "~/types";

// api
import { getWalletList } from "~/services/wallet";
import { getTransactionList } from "~/services/transaction";

// utils
import { getCurrentWeek, isObjectEmpty } from "~/utils";

// Styles
import {
  StyledAddBtn,
  StyledNotFound,
  StyledErrorMessage,
} from "~/components/Layout";
import {
  StyledList,
  StyledLoading,
  StyledDateList,
  StyledWalletList,
  StyledListWrapper,
  StyledLabelWrapper,
  StyledLoadingMoreTransaction,
} from "./TransactionList.styled";
import { StyledInfoLabel, StyledModal } from "~/components/Layout/LayoutInfo";

const { currentYear, currentMonth } = getCurrentWeek();
const getSessionMonth = () => sessionStorage.getItem("currentMonth") || "null";

interface TransactionListProps {}

const TransactionList: FC<TransactionListProps> = () => {
  const [month, setMonth] = useState<{
    year: string | number;
    month: string | number;
  }>(() => {
    /**
     * check if user have chosen month, if not, return current month
     */
    const sessionMonth = JSON.parse(getSessionMonth());
    if (sessionMonth) {
      return sessionMonth;
    }
    return { year: currentYear, month: currentMonth };
  });
  const [walletId, setWalletId] = useState<string>("");
  const [walletCurrency, setWalletCurrency] = useState<string>("");
  const [openAdd, setOpenAdd] = useState<boolean>(false);

  const weekNum: number = 5;

  // const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const listBorder = useRef<HTMLDivElement>(null);

  // for auto loader more transaction when user scroll to bottom
  let isInfiniteScroll = true;
  const listHeight = listBorder.current?.offsetHeight || 0;
  const scrollPosition = listBorder.current?.scrollHeight || 0;

  // restore current month and wallet chosen when component mount
  useEffect(() => {
    const currentMonth = JSON.parse(getSessionMonth());
    const walletId = sessionStorage.getItem("walletId") || "";
    const walletCurrency = sessionStorage.getItem("walletCurrency") || "";
    if (walletId) {
      setWalletId(walletId);
      setWalletCurrency(walletCurrency);
    }

    if (currentMonth) {
      setMonth(currentMonth);
    }
  }, []);

  const walletListQuery = useQuery({
    queryKey: QUERY_KEY.WALLET.WALLET_LIST,
    queryFn: getWalletList,
    onError(error: TQueryError) {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const walletList = walletListQuery?.data || [];

  /** if user have chosen wallet, we do not reset this */
  if (!walletId && walletList?.length) {
    setWalletId(walletList[0]?.id);
    setWalletCurrency(walletList[0]?.currencyId);
  }

  const transactionsQuery = useInfiniteQuery({
    queryKey: [
      QUERY_KEY.TRANSACTION.TRANSACTION_LIST,
      JSON.stringify(month),
      walletId,
    ],
    queryFn: ({ pageParam }) =>
      getTransactionList({
        walletId,
        ...month,
        week: pageParam,
      }),
    enabled: !!walletId,
    getNextPageParam: (lastPage, pages) => {
      if (walletId) {
        if (pages.length < 5) {
          return weekNum - pages.length;
        }
        isInfiniteScroll = false;
        return undefined;
      }
    },

    onError(error: TQueryError) {},
  });

  let transactionList: any[] = [];
  transactionsQuery?.data?.pages.forEach((page) => {
    if (!isObjectEmpty(page)) {
      transactionList.push(Object.entries(page));
    }
  });

  const walletListItem = walletList.map((item) => (
    <MenuItem value={`${item.id}+${item.currencyId}`}>{item.name}</MenuItem>
  ));

  // methods
  const handleChangeMonth = (event: SyntheticEvent, value: string) => {
    setMonth(JSON.parse(value));
    sessionStorage.setItem("currentMonth", value);
  };

  const handleChangeWallet = (event: SelectChangeEvent<any>) => {
    const [walletId, walletCurrency] = event.target.value?.split("+");
    setWalletId(walletId);
    setWalletCurrency(walletCurrency);
    sessionStorage.setItem("walletId", walletId);
    sessionStorage.setItem("walletCurrency", walletCurrency);
  };

  const handleLoadMoreTransactions = () => {
    if (walletId && transactionsQuery?.data?.pages.length) {
      transactionsQuery.fetchNextPage();
    }
  };

  /** When list rendered, check it do not have any transaction
   * or transaction list not full list's height
   * then continue load more transactions
   * */
  if (listHeight >= scrollPosition && isInfiniteScroll) {
    handleLoadMoreTransactions();
  }

  const handleScrollList = () => {
    const listTop = listBorder.current?.scrollTop || 0;
    const listHeight = listBorder.current?.offsetHeight || 0;
    const scrollPosition = listBorder.current?.scrollHeight || 0;
    if (listTop + listHeight + 1 >= scrollPosition && isInfiniteScroll) {
      handleLoadMoreTransactions();
    }
  };

  const handleOpenAddModal = () => {
    setOpenAdd(true);
  };

  const handleCloseAddModal = () => {
    setOpenAdd(false);
  };

  return (
    <StyledListWrapper>
      <StyledLabelWrapper>
        <StyledInfoLabel>Transaction List</StyledInfoLabel>
        <StyledWalletList
          name={"wallet"}
          id={"wallet"}
          value={`${walletId}+${walletCurrency}`}
          onChange={handleChangeWallet}
        >
          {walletListItem}
        </StyledWalletList>
      </StyledLabelWrapper>
      <StyledList>
        <MonthTab
          key={1}
          currentMonth={month}
          onChangeMonth={handleChangeMonth}
        />
        <StyledDateList key={2} ref={listBorder} onScroll={handleScrollList}>
          <Choose>
            <When condition={transactionsQuery.isLoading}>
              <StyledLoading>
                <HashLoading />
              </StyledLoading>
            </When>
            <When condition={transactionsQuery.isError}>
              <StyledLoading>
                <StyledErrorMessage>
                  {transactionsQuery?.error?.message}
                </StyledErrorMessage>
              </StyledLoading>
            </When>
            <When condition={transactionsQuery.isSuccess}>
              {transactionList.length === 0 ? (
                <StyledLoading>
                  <StyledNotFound>No transaction found!</StyledNotFound>
                </StyledLoading>
              ) : (
                transactionList?.map((item) =>
                  item?.map(([date, data]: [string, any[]]) => (
                    <DayTransactions
                      dateString={date}
                      transactionData={data}
                      walletId={walletId}
                      walletCurrency={walletCurrency}
                      key={date}
                    />
                  ))
                )
              )}
              <If condition={transactionsQuery.isFetchingNextPage}>
                <StyledLoadingMoreTransaction>
                  <BarLoading />
                </StyledLoadingMoreTransaction>
              </If>
            </When>
          </Choose>
        </StyledDateList>
        <StyledAddBtn
          key={3}
          onClick={handleOpenAddModal}
          variant={"outlined"}
          icon={<AddIcon />}
        >
          Create new Transaction
        </StyledAddBtn>
      </StyledList>
      <StyledModal open={openAdd}>
        <div>
          <TransactionAdd walletId={walletId} onCancel={handleCloseAddModal} />
        </div>
      </StyledModal>
    </StyledListWrapper>
  );
};

export default TransactionList;
